import type { TFunction } from 'i18next';
import type { ResolvedPhaseLabels } from '../../../../helpers/phase-labels/phase-labels.type';
import {
  graphLightRed,
  graphPictonBlue,
  graphSandyBrown,
  graphSunglow,
  graphTropicalIndigo,
  pinkBase,
  primaryBase,
} from '../../../../styles/design-tokens';
import { Phase, PhaseMetadataItem } from './flow-by-phase-tab.type';

const PHASES: Phase[] = [
  Phase.Backlog,
  Phase.Blocked,
  Phase.Wip,
  Phase.Review,
  Phase.Test,
  Phase.Deployable,
  Phase.Done,
];

/**
 * Get localized phase labels - executed at runtime to support language changes.
 *
 * When `overrides` is provided (typically the output of `resolvePhaseLabels`
 * fed by a backend `phases` companion array), per-workflow display labels
 * win over the `common.workflow_steps.*` translations. Without overrides,
 * the function returns the i18n defaults — preserving prior behavior.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`.
 * @param {ResolvedPhaseLabels} [overrides] - Optional resolved per-workflow
 *   display labels keyed by canonical phase key.
 * @returns {Record<Phase, string>} A record of phase labels keyed by phase.
 */
const getPhasesLabels = (t: TFunction, overrides?: ResolvedPhaseLabels): Record<Phase, string> => ({
  [Phase.Backlog]: overrides?.[Phase.Backlog] ?? t('common.workflow_steps.backlog'),
  [Phase.Blocked]: overrides?.[Phase.Blocked] ?? t('common.workflow_steps.waiting'),
  [Phase.Wip]: overrides?.[Phase.Wip] ?? t('common.workflow_steps.in_progress'),
  [Phase.Review]: overrides?.[Phase.Review] ?? t('common.workflow_steps.in_review'),
  [Phase.Test]: overrides?.[Phase.Test] ?? t('common.workflow_steps.in_test'),
  [Phase.Deployable]: overrides?.[Phase.Deployable] ?? t('common.workflow_steps.deployable'),
  [Phase.Done]: overrides?.[Phase.Done] ?? t('common.workflow_steps.done'),
});

/**
 * Get phase metadata with localized labels - executed at runtime to support
 * language changes and per-workflow display-label overrides.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`.
 * @param {ResolvedPhaseLabels} [overrides] - Optional resolved per-workflow
 *   display labels keyed by canonical phase key. When supplied, override
 *   labels win over the i18n defaults for the matching keys.
 * @returns {PhaseMetadataItem[]} An array of phase metadata items with
 *   their corresponding labels, colors, and descriptions.
 */
const getPhasesMetadata = (t: TFunction, overrides?: ResolvedPhaseLabels): PhaseMetadataItem[] => {
  const labels = getPhasesLabels(t, overrides);

  return [
    {
      id: Phase.Backlog,
      label: labels[Phase.Backlog],
      color: graphTropicalIndigo,
      description: 'Not started',
    },
    {
      id: Phase.Blocked,
      label: labels[Phase.Blocked],
      color: graphLightRed,
      description: 'Held up',
    },
    {
      id: Phase.Wip,
      label: labels[Phase.Wip],
      color: graphSandyBrown,
      description: 'Started',
    },
    {
      id: Phase.Review,
      label: labels[Phase.Review],
      color: graphSunglow,
      description: 'Code review',
    },
    {
      id: Phase.Test,
      label: labels[Phase.Test],
      color: pinkBase,
      description: 'Acceptance',
    },
    {
      id: Phase.Deployable,
      label: labels[Phase.Deployable],
      color: graphPictonBlue,
      description: 'Staged',
    },
    {
      id: Phase.Done,
      label: labels[Phase.Done],
      color: primaryBase,
      description: 'Work finished',
    },
  ];
};

export { getPhasesMetadata, PHASES };
